import React from 'react';
import { BANKS_QUERY, PAGED_EXCHANGE_RATES_QUERY } from '../queries';
import { Card } from '@totalsoft/rocket-ui';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ExchangeRateItem from './ExchangeRateItem';
import {
  EXCHANGE_RATE_DELETED,
  EXCHANGE_RATE_IMPORT_STARTED,
  EXCHANGE_RATE_IMPORT_SUCCEDED,
  EXCHANGE_RATE_IMPORT_FAILED
} from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import ExchangeRateModalComponent from '../../edit/components/ExchangeRateModalComponent';
import PropTypes from 'prop-types';
import useSubscription from 'hooks/apollo/useSubscription';

export const exchangeRatesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'fromExchangeRateDate',
      caption: 'ExchangeRate.FromExchangeRateDate',
      type: 'date'
    },
    {
      name: 'toExchangeRateDate',
      caption: 'ExchangeRate.ToExchangeRateDate',
      type: 'date'
    },
    {
      name: 'refCurrencyId',
      caption: 'ExchangeRate.RefCurrencyCode',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'currencyId',
      caption: 'ExchangeRate.CurrencyCode',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'bankId',
      caption: 'ExchangeRate.BankName',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'exchangeRate',
      caption: 'ExchangeRate.ExchangeRate',
      type: 'number'
    }
  ],
  orderByFields: [
    {
      caption: 'ExchangeRate.ExchangeRateDate',
      value: 'ExchangeRateDate'
    },
    {
      caption: 'ExchangeRate.BankName',
      value: 'BankName'
    },
    {
      caption: 'ExchangeRate.ExchangeRate',
      value: 'ExchangeRate'
    }
  ],
  pager: {
    orderBy: 'ExchangeRateDate',
    direction: 'desc'
  }
});

const ExchangeRateListBrowser = ({ handleCanImportExchangeRate }) => {
  const addToast = useToast();
  const { t } = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_EXCHANGE_RATES_QUERY,
        name: 'pagedExchangeRates'
      },
      collectionsOptions: [
        {
          queryOptions: { query: CURRENCIES_DICTIONARY_QUERY },
          name: 'currenciesDictionary',
          identifier: 'currencyId',
          collectionType: CollectionType.Filter,
          valueKey: 'id',
          labelKey: 'name'
        },
        {
          queryOptions: { query: CURRENCIES_DICTIONARY_QUERY },
          name: 'currenciesDictionary',
          identifier: 'refCurrencyId',
          collectionType: CollectionType.Filter,
          valueKey: 'id',
          labelKey: 'name'
        },
        {
          queryOptions: { query: BANKS_QUERY },
          name: 'banks',
          identifier: 'bankId',
          collectionType: CollectionType.Filter,
          valueKey: 'bankId',
          labelKey: 'bankName'
        }
      ]
    }),
    []
  );

  useSubscription(EXCHANGE_RATE_DELETED, {
    onData: ({ client }) => {
      addToast(t('ExchangeRate.ExchangeRateDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedExchangeRates' });
      client.cache.gc();
    }
  });

  useSubscription(EXCHANGE_RATE_IMPORT_STARTED, {
    onError: _ => {
      handleCanImportExchangeRate(true);
    },
    onData: () => {
      addToast(t('ExchangeRate.ExchangeRateStartImport'), 'success');
    }
  });

  useSubscription(EXCHANGE_RATE_IMPORT_FAILED, {
    onError: _ => {
      handleCanImportExchangeRate(true);
    },
    onData: message => {
      handleCanImportExchangeRate(true);
      addToast(t(message.data.data.exchangeRateImportFailed.failMessage), 'error');
    }
  });

  useSubscription(EXCHANGE_RATE_IMPORT_SUCCEDED, {
    onError: _ => {
      handleCanImportExchangeRate(true);
    },
    onData: ({ client }) => {
      addToast(t('ExchangeRate.ExchangeRateImportSucceeded'), 'success');
      handleCanImportExchangeRate(true);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedExchangeRates' });
      client.cache.gc();
    }
  });

  const columns = useMemo(
    () => [
      { name: 'exchangeRateDate', caption: 'ExchangeRate.ExchangeRateDate' },
      { name: 'bankName', caption: 'ExchangeRate.BankName' },
      { name: 'refCurrencyCode', caption: 'ExchangeRate.RefCurrencyCode' },
      { name: 'currencyCode', caption: 'ExchangeRate.CurrencyCode' },
      { name: 'exchangeRate', caption: 'ExchangeRate.ExchangeRate' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='exchange-rates-browser' store={exchangeRatesStore} ItemComponent={ExchangeRateItem} columns={columns} />
      <ExportModalComponent store={exchangeRatesStore} options={fetchingOptions} />
      <ExchangeRateModalComponent />
      <ApolloFetchingComponent store={exchangeRatesStore} options={fetchingOptions} />
    </Card>
  );
};

ExchangeRateListBrowser.propTypes = {
  handleCanImportExchangeRate: PropTypes.func.isRequired
};

export default ExchangeRateListBrowser;
